import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaSwimmingPool, FaGasPump, FaTint, FaBuilding, FaBasketballBall,
  FaTableTennis, FaDumbbell, FaChess, FaChild, FaHospitalAlt,
  FaShoppingBasket, FaStore, FaSchool, FaMapMarkerAlt,
  FaBed, FaBath, FaDoorOpen, FaClock, FaPhone, FaEnvelope,
  FaWhatsapp, FaUserShield, FaHandshake, FaArrowLeft,
  FaComments, FaCopy, FaCheck
} from 'react-icons/fa';
import MediaGallery from '../../components/media/MediaGallery';
import Avatar from '../../components/notes/Avatar';
import DOMPurify from 'dompurify';
import TimeAgo from '../../components/timeAgo/TimeAgo';
import styles from './PropertyDetailCard.module.css';

const featureIcons = {
  swimming_pool: { icon: <FaSwimmingPool />, label: 'Swimming Pool' },
  gas_connection: { icon: <FaGasPump />, label: 'Gas Connection' },
  ro: { icon: <FaTint />, label: 'RO Water' },
  club_house: { icon: <FaBuilding />, label: 'Club House' },
  basketball_court: { icon: <FaBasketballBall />, label: 'Basketball Court' },
  tennis_court: { icon: <FaTableTennis />, label: 'Tennis Court' },
  gym: { icon: <FaDumbbell />, label: 'Gym' },
  indoor_games: { icon: <FaChess />, label: 'Indoor Games' },
  child_play_area: { icon: <FaChild />, label: 'Child Play Area' },
  hospital_near_by: { icon: <FaHospitalAlt />, label: 'Hospital Nearby' },
  mall_near_by: { icon: <FaShoppingBasket />, label: 'Mall Nearby' },
  market_near_by: { icon: <FaStore />, label: 'Market Nearby' },
  school_near_by: { icon: <FaSchool />, label: 'School Nearby' },
};

const PropertyDetailCard = ({
  name,
  address,
  land_mark,
  area,
  pin_code,
  city,
  state,
  country,
  structure,
  description = '',
  feature = {},
  price,
  propertyPurpose,
  propertyType,
  property_sub_type,
  property_sub_sub_type,
  photo,
  creater,
  images = [],
  videos = [],
  attachments = [],
  createdOn,
  isPublished,
  user_type,
  is_address_added,
  is_become_a_partner,
  is_email_verified,
  is_phone_verified,
  is_whatsapp_number,
  isAdmin,
  backBtn,
  discussionPath
}) => {
  const navigate = useNavigate();
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const cleanDescription = (description || '').trim();
  const descriptionWords = cleanDescription ? cleanDescription.split(' ') : [];
  const isLongDescription = descriptionWords.length > 60;
  const displayedDescription =
    isLongDescription && !showFullDescription
      ? descriptionWords.slice(0, 60).join(' ') + '...'
      : cleanDescription;

  // Format Price in Indian currency
  const formattedPrice =
    price !== undefined && price !== null && price !== ''
      ? isNaN(Number(price))
        ? price
        : `₹ ${Number(price).toLocaleString('en-IN')}`
      : null;

  const hasStructure =
    structure &&
    (Number(structure.bed_rooms) > 0 ||
      Number(structure.bath_rooms) > 0 ||
      Number(structure.rooms) > 0);

  const activeFeatures = feature
    ? Object.entries(featureIcons).filter(([key]) => Boolean(feature[key]))
    : [];

  const locationDetails = [city, state, country].filter(Boolean).join(', ');

  return (
    <div className={styles.pageWrapper}>
      {/* 1. Main Property Overview Card */}
      <div className={styles.sectionCard}>
        {/* Top Back Navigation Bar */}
        {(backBtn || discussionPath) && (
          <div className={styles.topNavHeader}>
            <div className={styles.topLeftNav}>
              {backBtn && (
                <Link to={backBtn} className={styles.backBtn}>
                  <FaArrowLeft /> Back to Properties
                </Link>
              )}
              <span className={styles.pageCategory}>Shared Property Details</span>
            </div>

            <div className={styles.headerActions}>
              {discussionPath && (
                <button
                  type="button"
                  className={styles.discussionActionBtn}
                  onClick={() => navigate(discussionPath)}
                  title="View Comments & Discussion"
                >
                  <FaComments />
                  <span>View Discussion</span>
                </button>
              )}

              <button
                type="button"
                className={styles.shareBtn}
                onClick={handleCopyLink}
                title="Copy property link"
              >
                {copied ? <FaCheck className="text-success" /> : <FaCopy />}
                <span>{copied ? 'Copied' : 'Share'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Media Gallery */}
        <MediaGallery
          images={images}
          promotion_videos={videos}
          attachments={attachments}
        />

        {/* Overview Details Section */}
        <div className={styles.overviewGrid}>
          {/* Left Column: Title, Address, Sublocation, Price & Badges */}
          <div className={styles.leftDetails}>
            <h1 className={styles.propertyTitle}>{name || 'Property Details'}</h1>

            {address && (
              <div className={styles.locationRow}>
                <FaMapMarkerAlt className={styles.locationIcon} />
                <span className={styles.primaryAddress}>{address}</span>
              </div>
            )}

            {/* Indented Sublocation lines matching visual hierarchy */}
            <div className={styles.subLocationList}>
              {land_mark && <div>Landmark: {land_mark}</div>}
              {area && <div>{area} Sq. ft.</div>}
              {(locationDetails || pin_code) && (
                <div>
                  {locationDetails} {pin_code ? `- ${pin_code}` : ''}
                </div>
              )}
            </div>

            {/* Price & Tag Pills */}
            <div className={styles.priceAndTagsRow}>
              {formattedPrice && (
                <div className={styles.priceValue}>{formattedPrice}</div>
              )}

              <div className={styles.tagPills}>
                {propertyPurpose && (
                  <span className={styles.pillBadge}>{propertyPurpose}</span>
                )}
                {propertyType && (
                  <span className={styles.pillBadge}>{propertyType}</span>
                )}
                {property_sub_type && (
                  <span className={styles.pillBadge}>{property_sub_type}</span>
                )}
                {property_sub_sub_type && (
                  <span className={styles.pillBadge}>{property_sub_sub_type}</span>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Listed By Card + Structure Horizontal Bar */}
          <div className={styles.rightSidebar}>
            {/* Listed By Card */}
            <div className={styles.listedByCard}>
              <div className={styles.creatorLeft}>
                <Avatar src={photo} icon={creater} size={42} />
                <div className={styles.creatorText}>
                  <span className={styles.creatorLabel}>Listed By</span>
                  <span className={styles.creatorName}>
                    {creater || 'Property Owner'}
                  </span>
                </div>
              </div>

              <div className="d-flex flex-column align-items-end gap-1">
                {createdOn && (
                  <div className={styles.creatorTime}>
                    <FaClock className="text-muted" />
                    <span>
                      Created <TimeAgo timestamp={createdOn} />
                    </span>
                  </div>
                )}
                {isAdmin && (
                  <span
                    className={`badge ${
                      isPublished ? 'bg-success' : 'bg-secondary'
                    }`}
                    style={{ fontSize: '0.75rem' }}
                  >
                    {isPublished ? 'Published' : 'Draft'}
                  </span>
                )}
              </div>
            </div>

            {/* Structure Metrics Row */}
            {hasStructure && (
              <div className={styles.structureBar}>
                {Number(structure.bed_rooms) > 0 && (
                  <div className={styles.structureItem}>
                    <FaBed className={styles.structureIcon} />
                    <span>
                      {structure.bed_rooms} Bedroom
                      {Number(structure.bed_rooms) > 1 ? 's' : ''}
                    </span>
                  </div>
                )}

                {Number(structure.bed_rooms) > 0 &&
                  Number(structure.bath_rooms) > 0 && (
                    <div className={styles.structureDivider} />
                  )}

                {Number(structure.bath_rooms) > 0 && (
                  <div className={styles.structureItem}>
                    <FaBath className={styles.structureIcon} />
                    <span>
                      {structure.bath_rooms} Bathroom
                      {Number(structure.bath_rooms) > 1 ? 's' : ''}
                    </span>
                  </div>
                )}

                {(Number(structure.bed_rooms) > 0 ||
                  Number(structure.bath_rooms) > 0) &&
                  Number(structure.rooms) > 0 && (
                    <div className={styles.structureDivider} />
                  )}

                {Number(structure.rooms) > 0 && (
                  <div className={styles.structureItem}>
                    <FaDoorOpen className={styles.structureIcon} />
                    <span>
                      {structure.rooms} Room
                      {Number(structure.rooms) > 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Description Section */}
        {cleanDescription && (
          <div className={styles.descriptionBlock}>
            <h3 className={styles.blockTitle}>Description</h3>
            <div className={styles.descriptionContent}>
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(displayedDescription),
                }}
              />
              {isLongDescription && (
                <button
                  type="button"
                  className={styles.readMoreLink}
                  onClick={() => setShowFullDescription(!showFullDescription)}
                >
                  {showFullDescription ? 'Read Less ▲' : 'Read More ▼'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 2. Facilities & Amenities Card */}
      {activeFeatures.length > 0 && (
        <div className={styles.sectionCard}>
          <h3 className={styles.blockTitle}>Facilities & Amenities</h3>
          <div className={styles.amenitiesGrid}>
            {activeFeatures.map(([key, { icon, label }]) => (
              <div key={key} className={styles.amenityCard}>
                <div className={styles.amenityIconSquare}>{icon}</div>
                <div className={styles.amenityName}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3. Admin User Summary Card */}
      {isAdmin && (
        <div className={styles.sectionCard}>
          <h3 className={styles.blockTitle}>User Summary</h3>
          <div className={styles.adminSummaryGrid}>
            {/* User Type */}
            <div className={styles.adminInfoBox}>
              <FaUserShield className={`${styles.adminIcon} text-primary`} />
              <div>
                <div className={styles.adminMetaLabel}>User Type</div>
                <div className="fw-semibold text-dark small">
                  {user_type || 'N/A'}
                </div>
              </div>
            </div>

            {/* Email Status */}
            <div className={styles.adminInfoBox}>
              <FaEnvelope className={`${styles.adminIcon} text-secondary`} />
              <div>
                <div className={styles.adminMetaLabel}>Email Status</div>
                <span
                  className={
                    is_email_verified
                      ? styles.statusBadgeSuccess
                      : styles.statusBadgeWarning
                  }
                >
                  {is_email_verified ? 'Verified' : 'Pending Verification'}
                </span>
              </div>
            </div>

            {/* Phone Status */}
            <div className={styles.adminInfoBox}>
              <FaPhone className={`${styles.adminIcon} text-secondary`} />
              <div>
                <div className={styles.adminMetaLabel}>Phone Status</div>
                <span
                  className={
                    is_phone_verified
                      ? styles.statusBadgeSuccess
                      : styles.statusBadgeDanger
                  }
                >
                  {is_phone_verified ? 'Verified' : 'Not Verified'}
                </span>
              </div>
            </div>

            {/* WhatsApp */}
            <div className={styles.adminInfoBox}>
              <FaWhatsapp className={`${styles.adminIcon} text-success`} />
              <div>
                <div className={styles.adminMetaLabel}>WhatsApp Number</div>
                <span
                  className={
                    is_whatsapp_number
                      ? styles.statusBadgeSuccess
                      : styles.statusBadgeMuted
                  }
                >
                  {is_whatsapp_number ? 'Available' : 'Not Provided'}
                </span>
              </div>
            </div>

            {/* Address Status */}
            <div className={styles.adminInfoBox}>
              <FaMapMarkerAlt className={`${styles.adminIcon} text-danger`} />
              <div>
                <div className={styles.adminMetaLabel}>Address Status</div>
                <span
                  className={
                    is_address_added
                      ? styles.statusBadgeSuccess
                      : styles.statusBadgeDanger
                  }
                >
                  {is_address_added ? 'Added' : 'Missing'}
                </span>
              </div>
            </div>

            {/* Partner Program */}
            <div className={styles.adminInfoBox}>
              <FaHandshake className={`${styles.adminIcon} text-info`} />
              <div>
                <div className={styles.adminMetaLabel}>Partner Program</div>
                <span
                  className={
                    is_become_a_partner
                      ? styles.statusBadgeInfo
                      : styles.statusBadgeMuted
                  }
                >
                  {is_become_a_partner ? 'Partner Approved' : 'Incomplete'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetailCard;
